// Core Estimation LLC - Master mock + content file
import {
  Building2, HardHat, Hammer, Ruler, Wrench, Zap, Layers, Home as HomeIcon,
  Brush, Box, Cpu, FileSpreadsheet, Calculator, ShieldCheck, Clock, Users,
  Award, Target, TrendingUp, CheckCircle2, Briefcase, Trees, ClipboardCheck,
  DollarSign, Activity, Gauge, FileText,
} from 'lucide-react';

export const LOGO_URL = '/logo.png';

export const company = {
  name: 'Core Estimation LLC',
  tagline: 'Precise Estimates. Solid Results.',
  short: 'Core Estimation',
  phone: '(888) 506-3888',
  phoneRaw: '+18885063888',
  email: 'support@coreestimation.us',
  addresses: [
    { label: 'Head Office', value: '1750 Tysons Blvd, Suite 1500, McLean, VA 22102' },
    { label: 'New York Office', value: '350 Fifth Avenue, 21st Floor, New York, NY 10118' },
    { label: 'California Office', value: '660 S Figueroa St, Suite 1200, Los Angeles, CA 90017' },
  ],
  social: { facebook: '#', linkedin: '#', instagram: '#', twitter: '#' },
};

export const heroStats = [
  { value: '99%', label: 'Bid Win Accuracy' },
  { value: '24+', label: 'Trades Covered' },
  { value: '1.2K', label: 'Projects Delivered' },
  { value: '24-48h', label: 'Fastest Turnaround' },
];

export const trades = [
  {
    slug: 'general-contractors',
    title: 'General Contractors',
    icon: HardHat,
    short: 'Full-stack cost estimates and CSI MasterFormat takeoffs that keep GCs ahead of every bid window.',
    image: 'https://images.unsplash.com/photo-1574757987642-5755f0839101?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHw0fHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXJ8ZW58MHx8fHwxNzc4NTgxNTQyfDA&ixlib=rb-4.1.0&q=85',
  },
  {
    slug: 'sub-contractors',
    title: 'Sub Contractors',
    icon: Wrench,
    short: 'Trade-specific quantity takeoffs and bid leveling so subs price tight and protect their margins.',
    image: 'https://images.unsplash.com/photo-1672748341520-6a839e6c05bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXJ8ZW58MHx8fHwxNzc4NTgxNTQyfDA&ixlib=rb-4.1.0&q=85',
  },
  {
    slug: 'architects-engineers',
    title: 'Architects & Engineers',
    icon: Ruler,
    short: 'Conceptual through CD-level cost estimates aligned with design intent and your firm’s standards.',
    image: 'https://images.unsplash.com/photo-1774600166588-1db444bda799?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHw0fHxhcmNoaXRlY3QlMjBlbmdpbmVlcnxlbnwwfHx8fDE3Nzg1ODE1NTB8MA&ixlib=rb-4.1.0&q=85',
  },
  {
    slug: 'developers-owners',
    title: 'Developers & Owners',
    icon: Building2,
    short: 'Pro-forma budgets, value-engineering studies and on-going cost validation across the project lifecycle.',
    image: 'https://images.unsplash.com/photo-1778062374926-f8432665f099?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxidWlsZGluZyUyMHJlc2lkZW50aWFsfGVufDB8fHx8MTc3ODU4MTU0OXww&ixlib=rb-4.1.0&q=85',
  },
];

export const services = [
  {
    slug: 'cost-estimation',
    title: 'Construction Cost Estimation',
    category: 'Core Service',
    icon: Calculator,
    summary: 'CSI MasterFormat estimates from conceptual budgets through hard-bid GMP — ±2% accuracy.',
    description:
      'Our Construction Cost Estimation service delivers fully detailed estimates from Class 5 conceptual budgets through Class 1 definitive estimates. Every estimate is broken down by CSI MasterFormat divisions, line-item priced with regional labor and material rates, and reviewed by a senior estimator before delivery.',
    bullets: [
      'CSI MasterFormat 2020 Edition divisions',
      'ASPE Class 1–5 estimates (conceptual through definitive)',
      'Crew-based labor productivity tied to your region',
      'Live RSMeans, supplier-quote and historical-database pricing',
      'OH&P, contingency, bond & insurance line items',
      'Bid-day phone support included',
    ],
    image: 'https://images.unsplash.com/photo-1542621334-a254cf47733d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBibHVlcHJpbnR8ZW58MHx8fHwxNzc4NTgxNTQyfDA&ixlib=rb-4.1.0&q=85',
  },
  {
    slug: 'material-takeoff',
    title: 'Material Takeoff Services',
    category: 'Core Service',
    icon: Box,
    summary: 'Item-by-item bill of materials for every CSI division — ready for supplier RFQs.',
    description:
      'Our Material Takeoff service produces a complete, supplier-ready bill of materials for the entire project, organized by CSI division and location. Quantities include real-world waste factors per Gypsum Association GA-216, ACI 117, and trade-specific norms.',
    bullets: [
      'Bill of materials in CSI / location / cost-code rollups',
      'Waste & cut-loss factors pre-applied',
      'Supplier-ready RFQ packages',
      'Sourced from PDF, DWG, RVT or BIM models',
      'Delivered in Excel + PDF + Bluebeam markup',
      'Reconciled to the finish schedule and drawings',
    ],
    image: 'https://images.unsplash.com/photo-1772442198624-4fc4d7281e89?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxjb25zdHJ1Y3Rpb24lMjBibHVlcHJpbnR8ZW58MHx8fHwxNzc4NTgxNTQyfDA&ixlib=rb-4.1.0&q=85',
  },
  {
    slug: 'quantity-takeoff',
    title: 'Quantity Takeoff Services',
    category: 'Core Service',
    icon: ClipboardCheck,
    summary: 'Precise quantity surveys by unit, area, length and volume — verifiable against the drawings.',
    description:
      'Our Quantity Takeoff service produces verifiable measurements for every trade — CY of concrete, SFCA of formwork, tons of rebar, LF of conduit, SF of drywall — each tied back to a drawing reference so your team can audit any number in seconds.',
    bullets: [
      'Quantities by Unit (EA), Area (SF), Length (LF), Volume (CY)',
      'Each line item references a drawing sheet + detail',
      'Color-coded Bluebeam markup files included',
      'Drawing-revision tracking on every line',
      'Compatible with PlanSwift, STACK, On-Screen Takeoff',
      'Issued in CSI format ready for pricing',
    ],
    image: 'https://images.unsplash.com/photo-1721244654392-9c912a6eb236?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjBibHVlcHJpbnR8ZW58MHx8fHwxNzc4NTgxNTQyfDA&ixlib=rb-4.1.0&q=85',
  },
  {
    slug: 'residential-estimating',
    title: 'Residential Estimating',
    category: 'General Contractor',
    icon: HomeIcon,
    summary: 'Detailed cost breakdowns for single-family homes, townhomes and luxury custom residences.',
    description:
      'We support custom home builders, renovation specialists and design-build firms across the US with CSI-format residential estimates, complete with line-item pricing, lender-ready summaries and full material takeoffs tied to your local labor rates.',
    bullets: [
      'Single-family, custom, and luxury residential',
      'Additions, remodels and ADUs',
      'Material + labor takeoffs in CSI format',
      'Lender, owner and bid-ready deliverables',
      'Cost-per-SF reconciliation',
      'Plan-options pricing for builders',
    ],
    image: 'https://images.unsplash.com/photo-1778062374926-f8432665f099?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxidWlsZGluZyUyMHJlc2lkZW50aWFsfGVufDB8fHx8MTc3ODU4MTU0OXww&ixlib=rb-4.1.0&q=85',
  },
  {
    slug: 'commercial-estimating',
    title: 'Commercial Estimating',
    category: 'General Contractor',
    icon: Briefcase,
    summary: 'Retail, office, hospitality and mixed-use estimates with full Schedule-of-Values support.',
    description:
      'From ground-up offices to interior fit-outs, we price the work the way owners and lenders expect — clean Schedule of Values, transparent assumptions and trade-by-trade backup ready for the GMP conversation.',
    bullets: [
      'Office, retail, hospitality, mixed-use',
      'Schedule of values and GMP estimates',
      'Bid leveling and subcontractor scope review',
      'Cost loading and milestone draws',
      'Tenant-improvement (TI) build-outs',
      'Core-and-shell pricing',
    ],
    image: 'https://images.unsplash.com/photo-1694521787162-5373b598945c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjBzaXRlfGVufDB8fHx8MTc3ODU4MTU0Mnww&ixlib=rb-4.1.0&q=85',
  },
  {
    slug: 'multi-family-estimating',
    title: 'Multi-Family & Apartments',
    category: 'General Contractor',
    icon: Building2,
    summary: 'Wood-frame, podium and Type I/II estimates for apartments, condos and student housing.',
    description:
      'We understand the unit-mix math behind multi-family. Deliverables include unit-type pricing rollups, building/phase breakdowns, site/common-area allocations and prevailing-wage labor when required.',
    bullets: [
      'Unit-type pricing rollups',
      'Building / phase / site allocations',
      'Type I, II, III, V construction',
      'Affordable housing & LIHTC compliant',
      'Podium / 5-over-2 expertise',
      'Conversion / adaptive-reuse pricing',
    ],
    image: 'https://images.unsplash.com/photo-1721244654392-9c912a6eb236?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjBibHVlcHJpbnR8ZW58MHx8fHwxNzc4NTgxNTQyfDA&ixlib=rb-4.1.0&q=85',
  },
  {
    slug: 'public-projects',
    title: 'Public Projects Estimation',
    category: 'General Contractor',
    icon: ShieldCheck,
    summary: 'DOT, federal, municipal and education estimates with prevailing-wage compliance.',
    description:
      'We price public work to the specs — Davis-Bacon, state prevailing wage, MWBE goals and agency cost manuals. Deliverables map cleanly to bid forms and unit-price schedules.',
    bullets: [
      'DOT, GSA, USACE, school districts',
      'Prevailing-wage labor loading',
      'MWBE / DBE participation tracking',
      'Unit-price bid form mapping',
      'Federal acquisition (FAR) compliance',
      'Cost-plus and lump-sum bid packages',
    ],
    image: 'https://images.pexels.com/photos/2383650/pexels-photo-2383650.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  },
  {
    slug: 'concrete-estimating',
    title: 'Concrete Estimating',
    category: 'Sub Contractor',
    icon: Layers,
    summary: 'Cast-in-place, tilt-up and flatwork takeoffs for CY, SFCA formwork, rebar and finishes.',
    description:
      'We quantify every cubic yard of concrete, every linear foot of formwork (SFCA — Square Feet Contact Area) and every pound of rebar — with crew-based labor productivity tuned to your region and waste factors per ACI 117.',
    bullets: [
      'CY of concrete + waste factors',
      'SFCA formwork and shoring',
      'Rebar tonnage by size (#3–#11)',
      'Flatwork finishes & sealers',
      'Pumping / placing / finishing labor',
      'Embeds, dowels, accessories',
    ],
    image: 'https://images.pexels.com/photos/4458210/pexels-photo-4458210.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  },
  {
    slug: 'drywall-takeoffs',
    title: 'Drywall Takeoff Estimating',
    category: 'Sub Contractor',
    icon: Layers,
    summary: 'Metal framing, gypsum board, tape, finish and accessories — by partition type and floor.',
    description:
      'We deliver wall-type-driven drywall takeoffs with separate quantities for framing, board, finish levels 1–5, insulation, and accessories like corner bead and reveals. Waste factors applied per Gypsum Association GA-216.',
    bullets: [
      'Partition-type (P1–P9+) rollups',
      'Finish levels 1–5 (per GA-214)',
      'Acoustic, shaft-wall, area-separation',
      'Soffits, bulkheads, ceilings',
      'Light-gauge metal framing quantities',
      'Joint compound, tape, accessories',
    ],
    image: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjBzaXRlfGVufDB8fHx8MTc3ODU4MTU0Mnww&ixlib=rb-4.1.0&q=85',
  },
  {
    slug: 'electrical-estimating',
    title: 'Electrical Estimating',
    category: 'Sub Contractor',
    icon: Zap,
    summary: 'NECA-unit power, lighting, low-voltage and gear takeoffs with assembly-based pricing.',
    description:
      'Our electrical estimators use NECA Manual of Labor Units for every assembly — branch circuits, feeders, switchgear, lighting and low-voltage. Outputs include detailed material lists ready for distributor RFQs and Accubid-compatible imports.',
    bullets: [
      'Service, gear and distribution',
      'Lighting fixtures and 0–10V controls',
      'Low-voltage, fire alarm, security',
      'NECA Manual labor units',
      'Accubid-compatible export',
      'Branch-circuit + feeder schedules',
    ],
    image: 'https://images.pexels.com/photos/21619174/pexels-photo-21619174.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  },
  {
    slug: 'flooring-estimating',
    title: 'Flooring Estimating',
    category: 'Sub Contractor',
    icon: Brush,
    summary: 'Carpet, LVT, tile, hardwood and resilient flooring takeoffs with cut-loss factoring.',
    description:
      'We measure every flooring type by room and finish schedule, applying real-world cut-loss percentages so you don’t bid short on material. Base, transitions and accessories are itemized separately.',
    bullets: [
      'Carpet, LVT, tile, hardwood, epoxy',
      'Cut-loss + waste factors',
      'Base, transitions, accessories',
      'Finish-schedule reconciliation',
      'Sound mat / underlayment quantities',
      'Demolition + prep work line items',
    ],
    image: 'https://images.pexels.com/photos/392031/pexels-photo-392031.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  },
  {
    slug: 'insulation-estimating',
    title: 'Insulation Estimating',
    category: 'Sub Contractor',
    icon: Box,
    summary: 'Batt, spray-foam, rigid and blow-in insulation takeoffs by R-value and assembly.',
    description:
      'From batt to closed-cell spray foam, we quantify insulation by assembly and R-value, with line items for vapor barriers, fire-stop and sealants. Pricing reflects current Q1-2026 supplier rates.',
    bullets: [
      'Batt, blown-in, rigid, spray foam',
      'R-value by assembly',
      'Vapor barriers + fire-stop',
      'Envelope and mechanical insulation',
      'IECC code-compliance check',
      'Air-sealing line items',
    ],
    image: 'https://images.unsplash.com/photo-1589939705384-5185137a7f0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXJ8ZW58MHx8fHwxNzc4NTgxNTQyfDA&ixlib=rb-4.1.0&q=85',
  },
  {
    slug: 'site-civil',
    title: 'Site & Civil Estimating',
    category: 'Sub Contractor',
    icon: Trees,
    summary: 'Earthwork, utilities, paving and landscape takeoffs with cut/fill grade modeling.',
    description:
      'Our site team handles cut/fill, underground utilities, paving sections and landscape with grade-model accuracy via AGTEK or Carlson. Erosion control and SWPPP line items included.',
    bullets: [
      'Cut / fill / borrow CY',
      'Wet and dry utilities',
      'Asphalt and concrete paving',
      'Hardscape and landscape',
      'Erosion control + SWPPP',
      'Stripping, grubbing, dewatering',
    ],
    image: 'https://images.pexels.com/photos/392031/pexels-photo-392031.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  },
];

export const whyChoose = [
  { icon: Target, title: '±2% Estimate Accuracy', text: 'Estimates routinely land within 2% of awarded bids thanks to live cost databases and crew-tuned productivity.' },
  { icon: Clock, title: '24–48 Hour Turnaround ⚡', text: 'The fastest turnaround in the industry. Most estimates and takeoffs are returned in just 24–48 hours — without compromising on accuracy.' },
  { icon: ShieldCheck, title: 'ASPE & AACE Aligned', text: 'Every estimate follows ASPE and AACE International best practices and is QA-reviewed by a senior estimator.' },
  { icon: Cpu, title: 'Modern Tooling', text: 'PlanSwift, Bluebeam Revu, Trimble Accubid, RSMeans, STACK — we use the stack your team already speaks.' },
  { icon: Users, title: 'Dedicated Estimator', text: 'You work with one senior estimator and one PM — not a ticket queue.' },
  { icon: TrendingUp, title: 'Higher Win Rate', text: 'Clients report a 35–50% improvement in bid-hit rate within the first six months.' },
];

export const process = [
  { step: '01', title: 'Send Drawings', text: 'Upload your plans, specs and bid documents in any common format — PDF, DWG, RVT, BIM, ZIP.' },
  { step: '02', title: 'Scope Confirmation', text: 'A senior estimator confirms scope, exclusions and deadline before pricing starts — fixed fee, no surprises.' },
  { step: '03', title: 'Digital Takeoff', text: 'We perform digital quantity takeoffs on PlanSwift, Bluebeam Revu or STACK — every line referenced to the drawings.' },
  { step: '04', title: 'Pricing & Backup', text: 'Quantities are priced using live RSMeans + regional supplier quotes + your historical job-cost data.' },
  { step: '05', title: 'QA Review', text: 'Senior-estimator QA pass catches gaps before the deliverable leaves our office.' },
  { step: '06', title: 'Delivery + Bid Support', text: 'Clean Excel + PDF + Bluebeam markups delivered. We stay on call through bid day for revisions & RFIs.' },
];

export const testimonials = [
  { name: 'Marcus Holloway', role: 'President, Holloway Commercial Builders', text: 'Core Estimation has been our outsourced preconstruction team for two years. Their estimates are tight, fast, and they back up every number.', rating: 5 },
  { name: 'Priya Raman', role: 'Estimating Manager, Northstar Electric', text: 'The electrical assemblies they use map directly into our Accubid database. Bid prep that used to take three days now takes one.', rating: 5 },
  { name: 'Jared Kim', role: 'Owner, Kim & Sons Concrete', text: 'Best concrete takeoffs we’ve received from any outside firm. Formwork SFCA was spot on.', rating: 5 },
  { name: 'Elena Vasquez', role: 'VP Development, Cobalt Realty Partners', text: 'Their multi-family pro-formas have been a key part of our equity packages for two new projects.', rating: 5 },
];

export const tools = [
  'PlanSwift', 'Bluebeam Revu', 'Trimble Accubid', 'RSMeans', 'STACK',
  'Sage Estimating', 'On-Screen Takeoff', 'ProEst', 'AGTEK', 'AutoCAD',
];

// ----- SAMPLES (PDFs served from backend /api/samples/{slug}.pdf) -----
export const samples = [
  {
    slug: 'residential-estimate',
    title: 'Residential Cost Estimate — Custom Single-Family Home',
    category: 'Cost Estimation',
    icon: HomeIcon,
    summary: '3,840 SF custom residence in Fairfax County, VA — full CSI MasterFormat breakdown with ±2% accuracy.',
    keyMetrics: [
      { label: 'Square Footage', value: '3,840 SF' },
      { label: 'Estimate Total', value: '$595,486' },
      { label: 'Cost / SF', value: '$155.07 / SF' },
      { label: 'ASPE Class', value: 'Class 2 (±2%)' },
    ],
    columns: ['Div', 'Description', 'Material', 'Labor', 'Sub', 'Total'],
    rows: [
      ['03', 'Concrete (footings, slab)', '$18,650', '$11,200', '$0', '$29,850'],
      ['06', 'Wood & Plastics (framing)', '$54,300', '$38,900', '$0', '$93,200'],
      ['07', 'Roofing & Insulation', '$11,200', '$0', '$28,400', '$39,600'],
      ['09', 'Finishes (drywall, paint, flooring)', '$14,600', '$0', '$48,200', '$62,800'],
      ['15', 'Plumbing', '$0', '$0', '$41,800', '$41,800'],
      ['15B', 'HVAC', '$0', '$0', '$38,400', '$38,400'],
      ['16', 'Electrical', '$0', '$0', '$34,600', '$34,600'],
    ],
  },
  {
    slug: 'commercial-estimate',
    title: 'Commercial Estimate — Class-A Office TI (22,400 RSF)',
    category: 'Cost Estimation',
    icon: Briefcase,
    summary: 'Tenant build-out, Arlington VA. Schedule of Values priced at ASPE Class 3 (±5%).',
    keyMetrics: [
      { label: 'Rentable SF', value: '22,400 RSF' },
      { label: 'Estimate Total', value: '$3,624,802' },
      { label: 'Cost / RSF', value: '$161.82 / RSF' },
      { label: 'ASPE Class', value: 'Class 3 (±5%)' },
    ],
    columns: ['CSI', 'Trade', 'Unit Cost', 'Subtotal'],
    rows: [
      ['02 41 00', 'Selective Demolition', '$2.85 / SF', '$63,840'],
      ['09 22 00', 'Light-Gauge Metal Framing', '$4.85 / LF', '$226,980'],
      ['09 29 00', 'Gypsum Board (Level 4)', '$3.10 / SF', '$286,440'],
      ['22 00 00', 'Plumbing', '$8.95 / SF', '$200,480'],
      ['23 00 00', 'HVAC (VAV mods + DDC)', '$22.40 / SF', '$501,760'],
      ['26 00 00', 'Electrical', '$28.80 / SF', '$645,120'],
      ['27 00 00', 'Low Voltage / Data / AV', '$6.20 / SF', '$138,880'],
    ],
  },
  {
    slug: 'concrete-takeoff',
    title: 'Concrete Quantity Takeoff — 5-over-2 Podium',
    category: 'Quantity Takeoff',
    icon: Layers,
    summary: 'Cast-in-place concrete takeoff with CY, SFCA formwork and rebar tonnage by bar size.',
    keyMetrics: [
      { label: 'Total Concrete', value: '1,936.9 CY' },
      { label: 'Net SFCA Forms', value: '24,325 SFCA' },
      { label: 'Reinforcing Steel', value: '160.33 Tons' },
      { label: 'Trade Subcontract', value: '$857,536' },
    ],
    columns: ['Item', 'Mix Design', 'Qty (CY)', 'Waste %', 'Ordered (CY)'],
    rows: [
      ['Footings — Spread', '3,000 PSI', '184.3', '5%', '193.5'],
      ['Slab on Grade', '4,000 PSI / 4" slump', '412.6', '4%', '429.1'],
      ['Foundation Walls', '4,000 PSI', '198.4', '5%', '208.3'],
      ['Columns', '5,000 PSI / Type II', '88.2', '5%', '92.6'],
      ['Shear Walls', '5,000 PSI', '146.9', '5%', '154.3'],
      ['Elevated Decks (PT)', '5,000 PSI / Light-wt', '684.4', '4%', '711.8'],
    ],
  },
  {
    slug: 'electrical-takeoff',
    title: 'Electrical Material & Labor Takeoff',
    category: 'Material Takeoff',
    icon: Zap,
    summary: '208Y/120V — 1,200A service, office TI. NECA Manual labor units applied throughout.',
    keyMetrics: [
      { label: 'Service', value: '1,200A 208Y/120V' },
      { label: 'Branch Circuits', value: '120+' },
      { label: 'Labor Hours', value: '1,942 hr' },
      { label: 'Trade Subcontract', value: '$398,156' },
    ],
    columns: ['Item', 'Description', 'Qty', 'Unit Mat.', 'Material Ext'],
    rows: [
      ['SWBD', '1,200A 208Y/120V Switchboard 42-ckt', '1 EA', '$18,450', '$18,450'],
      ['XFMR', '75 kVA Dry-Type 480→208/120V', '2 EA', '$4,280', '$8,560'],
      ['PNL-A', '225A 84-ckt MLO Panelboard', '3 EA', '$2,840', '$8,520'],
      ['L1', '2x4 LED Troffer 4000K 40W', '186 EA', '$118', '$21,948'],
      ['EMT', '1/2" EMT Conduit', '18,400 LF', '$1.10', '$20,240'],
      ['THHN', '#12 THHN-CU Wire', '48,200 LF', '$0.34', '$16,388'],
    ],
  },
  {
    slug: 'drywall-takeoff',
    title: 'Drywall & Metal Framing Takeoff',
    category: 'Quantity Takeoff',
    icon: Layers,
    summary: 'CSI 09 22 00 + 09 29 00 — partition-type rollup with Level 4 finish.',
    keyMetrics: [
      { label: 'Gypsum Board', value: '98,868 SF' },
      { label: 'Steel Studs', value: '70,632 LF' },
      { label: 'Mineral-Wool Batt', value: '5,733 SF' },
      { label: 'Trade Subcontract', value: '$355,143' },
    ],
    columns: ['Wall Type', 'Description', 'Linear Ft', 'SF (2-side)'],
    rows: [
      ['P1', '3-5/8" Std, 5/8" GWB ea side', '1,420 LF', '26,980 SF'],
      ['P2', '3-5/8" Std, full height to deck', '380 LF', '9,880 SF'],
      ['P3', '6" Std, STC-50 acoustic', '420 LF', '10,920 SF'],
      ['P4', '6" Shaft-wall, 1" liner', '180 LF', '2,520 SF'],
      ['P5', '3-5/8" furring (CMU)', '640 LF', '8,320 SF'],
    ],
  },
  {
    slug: 'material-takeoff',
    title: 'Full Material Takeoff — Residential New Build',
    category: 'Material Takeoff',
    icon: Box,
    summary: 'Complete bill of materials across all trades for a 3,840 SF custom residence.',
    keyMetrics: [
      { label: 'Trades Covered', value: '14 CSI Divs' },
      { label: 'Line Items', value: '320+' },
      { label: 'Drawing References', value: 'Every Line' },
      { label: 'Waste Factors', value: 'Pre-applied' },
    ],
    columns: ['Item', 'Spec', 'Qty', 'Unit'],
    rows: [
      ['Ready-mix concrete 3,000 PSI', '.50 W/C, 5% air', '38.4', 'CY'],
      ['2x6 SPF Stud-grade', '92" PET', '320', 'EA'],
      ['I-Joist 11-7/8" TJI 230', '24\'', '72', 'EA'],
      ['Architectural Shingles 30-yr', 'GAF Timberline HDZ', '36.4', 'SQ'],
      ['PEX-A tubing 1/2"', 'Uponor', '1,140', 'LF'],
      ['200A Main Panel', 'Square-D QO 40-space', '1', 'EA'],
      ['Engineered hardwood', '7-1/2" white oak', '1,840', 'SF'],
    ],
  },
];

// ----- SEO-RICH BLOG -----
export const blogPosts = [
  {
    slug: 'precision-electrical-estimating-2026',
    title: 'Precision Electrical Estimating: Why “Guesstimating” Is Killing Your Margins in 2026',
    metaTitle: 'Precision Electrical Estimating | NECA Labor Units | Core Estimation',
    metaDesc: 'Learn why precision electrical estimating with NECA labor units protects contractor margins, and how assembly-based pricing wins more bids in 2026.',
    category: 'Electrical Estimating',
    date: 'April 18, 2026',
    readTime: '6 min',
    excerpt: 'In the electrical contracting world, the difference between a profitable year and a brutal one usually comes down to one thing — bid-day discipline.',
    image: 'https://images.pexels.com/photos/21619174/pexels-photo-21619174.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    body: [
      'Most electrical contractors lose money not on the job, but the moment the bid is submitted. A 2% miss on labor units on a $1.2M project is a $24,000 swing — usually right out of the net.',
      'Precision electrical estimating starts with assemblies, not gut feel. Build a labor library that reflects your real productivity, your supplier pricing, and your geographic labor cost. The NECA Manual of Labor Units is the industry baseline — but the best electrical estimators tune those numbers with their own historical job-cost data.',
      'When you guesstimate, you also lose negotiating leverage. The general contractor knows when your number is soft, and they will hold you to it on change orders.',
      'The fix is mechanical, not mystical: invest in tooling (Accubid, ConEst, or even a tuned Excel template), invest in a senior estimator, and treat your historical job-cost data as the single source of truth. Every project that closes should refine the library that prices the next one.',
      'Three takeaways for 2026: (1) move every recurring item into an assembly, (2) reconcile your final job cost to the bid at least quarterly, and (3) stop bidding work where your labor data is thin — that\'s the work that bleeds margin.',
    ],
  },
  {
    slug: 'construction-estimate-secrets-cost-prediction',
    title: 'Construction Estimate Secrets: How Pros Predict Costs With ±2% Precision',
    metaTitle: 'Construction Cost Estimate ±2% Precision Framework | Core Estimation',
    metaDesc: 'The framework experienced estimators use to land construction cost estimates within ±2% of awarded bids — including database hygiene, contingency math and QA.',
    category: 'Construction Estimating',
    date: 'April 02, 2026',
    readTime: '8 min',
    excerpt: 'A precise construction estimate acts as the heartbeat of any winning bid. Here is the framework experienced estimators use to consistently land within 2%.',
    image: 'https://images.unsplash.com/photo-1772442198624-4fc4d7281e89?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHw0fHxjb25zdHJ1Y3Rpb24lMjBibHVlcHJpbnR8ZW58MHx8fHwxNzc4NTgxNTQyfDA&ixlib=rb-4.1.0&q=85',
    body: [
      'Great estimators build a personal database of unit costs they trust, then re-baseline it every quarter. RSMeans is a starting point, not a finish line.',
      'They separate the price into material, labor, equipment and subcontract — never lumping it into a square-foot guess.',
      'They model risk explicitly with contingency lines, not by padding individual unit prices. The ASPE classification (Class 5 to Class 1) tells you exactly how much contingency to carry at each stage.',
      'And they QA every estimate with a second pair of eyes before it goes out the door. A 30-minute review by a senior estimator catches the same errors that a $200K change order would have surfaced.',
      'Finally — they reconcile. Every closed-out job feeds back into the database, so the next estimate is sharper than the last. This is the single most important discipline in cost estimation.',
    ],
  },
  {
    slug: 'material-takeoff-vs-quantity-takeoff',
    title: 'Material Takeoff vs. Quantity Takeoff: What\'s the Difference, and Why It Matters',
    metaTitle: 'Material Takeoff vs Quantity Takeoff Explained | Core Estimation',
    metaDesc: 'Material takeoffs and quantity takeoffs are not the same thing. Here is the precise difference, with examples for concrete, drywall, and electrical work.',
    category: 'Material Takeoff',
    date: 'March 28, 2026',
    readTime: '5 min',
    excerpt: 'These two terms get used interchangeably — and they shouldn\'t be. The difference impacts how you price, procure and protect your margin.',
    image: 'https://images.pexels.com/photos/4458205/pexels-photo-4458205.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    body: [
      'A quantity takeoff measures everything the drawings define — every cubic yard of concrete, every linear foot of conduit, every square foot of drywall. The output is a list of measurements, not a list of products.',
      'A material takeoff is what you procure. It converts the quantities into ordered units — sheets of drywall, sticks of EMT, tons of rebar — with waste factors and packaging applied.',
      'On a 1,200 LF wall, the quantity takeoff says 1,200 LF of stud framing. The material takeoff says 1,290 LF of 25-gauge steel studs after an 8% waste factor, plus track, plus screws.',
      'Why does this matter? Because procurement, pricing, and bid scope are all keyed off different versions of the same underlying measurement. Getting the conversion right is what separates a senior estimator from a junior one.',
    ],
  },
  {
    slug: 'yard-of-concrete-cost-2026',
    title: 'How Much Does a Yard of Concrete Cost in 2026? Regional Pricing Breakdown',
    metaTitle: '2026 Concrete Cost per Yard | Regional Pricing | Core Estimation',
    metaDesc: 'National and regional 2026 pricing for ready-mix concrete, including mix design, haul distance, short-load fees and labor placement costs.',
    category: 'Concrete Estimation',
    date: 'March 24, 2026',
    readTime: '5 min',
    excerpt: 'As of Q1 2026, national average pricing for ready-mix concrete sits between $165–$210 per cubic yard — but the real number depends on three variables.',
    image: 'https://images.pexels.com/photos/4458205/pexels-photo-4458205.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    body: [
      'The biggest driver is mix design. A 3,000 PSI baseline mix in the mid-Atlantic averages $172/CY in Q1-2026. Bump to a 4,000 PSI structural mix with admixtures and you\'re at $185–$195/CY.',
      'Next is haul distance. Anything over 25 minutes from the plant gets surcharged at most ready-mix producers. Long-haul (>45 min) can add $18–$30/CY.',
      'Finally — short-load fees. Anything under 8 CY per truck and you\'re paying a short-load premium of $80–$150 per truck. On a small footing pour, this can blow your CY price by 25%.',
      'Don\'t forget placement labor. Even at $185/CY material, finished and placed concrete with formwork, rebar and finishing typically lands at $480–$620/CY in commercial work.',
    ],
  },
  {
    slug: 'csi-masterformat-2020-explained',
    title: 'CSI MasterFormat 2020 Explained: A Practical Guide for Estimators',
    metaTitle: 'CSI MasterFormat 2020 Guide for Construction Estimators | Core Estimation',
    metaDesc: 'A working estimator\'s guide to CSI MasterFormat 2020 — division-by-division summary, what changed from 2018, and how to structure your bid documents.',
    category: 'Construction Estimating',
    date: 'March 14, 2026',
    readTime: '9 min',
    excerpt: 'If your estimates aren\'t organized in CSI MasterFormat 2020, you\'re making your owners, lenders, and bidders work harder than they need to.',
    image: 'https://images.unsplash.com/photo-1721244654392-9c912a6eb236?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjBibHVlcHJpbnR8ZW58MHx8fHwxNzc4NTgxNTQyfDA&ixlib=rb-4.1.0&q=85',
    body: [
      'MasterFormat is the standard organizational structure for commercial construction specifications and estimates in North America, maintained by CSI and CSC.',
      'The 2020 edition organizes work into 49 numbered divisions, grouped under Procurement & Contracting (Div 00), General Requirements (Div 01), and Construction (Div 02–49).',
      'For estimators, the key divisions are 03 (Concrete), 04 (Masonry), 05 (Metals), 06 (Wood), 07 (Thermal & Moisture), 08 (Openings), 09 (Finishes), 21 (Fire Suppression), 22 (Plumbing), 23 (HVAC), 26 (Electrical), 27 (Communications), and 28 (Electronic Safety).',
      'Each estimate line should reference a section number (e.g., 03 30 00 for Cast-in-Place Concrete) so subcontractors can quickly map their scope. This single discipline cuts bid-leveling time in half.',
    ],
  },
  {
    slug: 'blow-in-insulation-estimators-2026',
    title: 'The Truth About Blow-In Insulation Estimators in 2026',
    metaTitle: 'Blow-In Insulation Cost Estimator 2026 | Realistic Pricing | Core Estimation',
    metaDesc: 'Why online blow-in insulation calculators are wrong, and what a real attic insulation estimate should include in 2026.',
    category: 'Insulation Estimating',
    date: 'March 11, 2026',
    readTime: '4 min',
    excerpt: 'Homeowners and contractors often struggle to predict attic upgrade costs because online calculators ignore three real-world variables.',
    image: 'https://images.unsplash.com/photo-1589939705384-5185137a7f0f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXJ8ZW58MHx8fHwxNzc4NTgxNTQyfDA&ixlib=rb-4.1.0&q=85',
    body: [
      'Calculators assume a clean, vacant attic — yours is rarely either.',
      'They also rarely include the cost of baffles, dam-rings around can lights, or air-sealing prep.',
      'A real estimate includes mobilization, prep, install and clean-up — not just material per square foot.',
      'For a 1,500 SF attic upgrade from R-19 to R-49 blown cellulose, expect $2,200–$3,400 installed in 2026, depending on access and existing conditions.',
    ],
  },
  {
    slug: 'best-residential-estimating-software-2026',
    title: 'Top 10 Best Software for Residential Construction Estimating in 2026',
    metaTitle: 'Best Residential Estimating Software 2026 | Reviews | Core Estimation',
    metaDesc: 'The 10 estimating tools serious home builders actually use in 2026 — PlanSwift, Buildertrend, STACK, ProEst and more, reviewed.',
    category: 'Residential Estimation',
    date: 'February 22, 2026',
    readTime: '10 min',
    excerpt: 'The key to a profitable 2026 isn\'t just winning more bids — it\'s winning the right ones. Here are the ten tools serious home builders actually use.',
    image: 'https://images.unsplash.com/photo-1778062374926-f8432665f099?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxidWlsZGluZyUyMHJlc2lkZW50aWFsfGVufDB8fHx8MTc3ODU4MTU0OXww&ixlib=rb-4.1.0&q=85',
    body: [
      'PlanSwift remains the workhorse for digital takeoffs — fast, affordable, and Excel-friendly.',
      'Buildertrend continues to dominate the residential workflow with scheduling and client communication baked in.',
      'For pure estimating, Sage Estimating and ProEst lead the pack with deep cost databases and assembly libraries.',
      'STACK is the cloud-first option growing fastest with mid-sized residential GCs.',
      'Bluebeam Revu is the markup standard for any project that exchanges PDFs.',
    ],
  },
  {
    slug: 'home-construction-estimator-reduce-costs',
    title: 'How a Home Construction Estimator Can Reduce Costs by 40%',
    metaTitle: 'Home Construction Estimator Reduces Costs 40% | Case Study | Core Estimation',
    metaDesc: 'A professional home construction estimator delivers a data-driven cost forecast that catches 40% of avoidable change orders before construction starts.',
    category: 'Residential Estimation',
    date: 'February 09, 2026',
    readTime: '7 min',
    excerpt: 'A Home Construction Estimator delivers a data-driven forecast of total cost — and used properly, it\'s the cheapest insurance in residential construction.',
    image: 'https://images.unsplash.com/photo-1542621334-a254cf47733d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBibHVlcHJpbnR8ZW58MHx8fHwxNzc4NTgxNTQyfDA&ixlib=rb-4.1.0&q=85',
    body: [
      'A real estimate forces you to interrogate every assumption before you swing a hammer.',
      'It catches scope gaps that would otherwise become change orders at 1.5× cost.',
      'And it gives you the leverage to negotiate fixed-price subs instead of T&M.',
      'On a recent 4,200 SF custom build, our pre-construction estimate caught $186,000 in scope gaps before the GC signed the construction contract — roughly 11% of total project cost.',
    ],
  },
  {
    slug: 'commercial-tenant-improvement-cost-2026',
    title: 'Commercial Tenant Improvement (TI) Cost Per Square Foot in 2026',
    metaTitle: 'Commercial TI Cost per Square Foot 2026 | Office Build-Out Pricing | Core Estimation',
    metaDesc: 'National and regional 2026 pricing for office, retail and restaurant tenant-improvement build-outs, by class and finish level.',
    category: 'Commercial Estimating',
    date: 'January 28, 2026',
    readTime: '8 min',
    excerpt: 'Office TI costs in 2026 range from $85 to $260 per RSF depending on class, base-building condition, and finish level.',
    image: 'https://images.unsplash.com/photo-1694521787162-5373b598945c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxjb25zdHJ1Y3Rpb24lMjBzaXRlfGVufDB8fHx8MTc3ODU4MTU0Mnww&ixlib=rb-4.1.0&q=85',
    body: [
      'Class-A office TI in the mid-Atlantic averages $145–$185/RSF for a standard build-out with open ceiling, glass-fronted offices and LVT floors.',
      'Restaurant TI is the most expensive — $260–$420/RSF typical due to hood systems, grease interceptors, refrigeration and FF&E.',
      'Retail TI lands at the lower end — $65–$95/RSF for a typical white-box-to-shell build-out without specialty finishes.',
      'Three variables drive 80% of the cost: MEP density, ceiling treatment, and millwork. Cut those carefully if budget is tight.',
    ],
  },
  {
    slug: 'drywall-takeoff-walkthrough',
    title: 'Drywall Takeoff Walkthrough: From P-Types to Priced Subcontract',
    metaTitle: 'Drywall Takeoff Step-By-Step | Partition Types to Final Bid | Core Estimation',
    metaDesc: 'A senior estimator walks through a complete drywall quantity takeoff — partition types, finish levels 1–5, waste factors, and the final priced subcontract.',
    category: 'Drywall Estimating',
    date: 'January 15, 2026',
    readTime: '6 min',
    excerpt: 'Drywall takeoffs look simple — and that\'s why they get them wrong. Here\'s how a senior estimator works one start-to-finish.',
    image: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjBzaXRlfGVufDB8fHx8MTc3ODU4MTU0Mnww&ixlib=rb-4.1.0&q=85',
    body: [
      'Step 1: Identify every partition type on sheet A-501. P1, P2, P3 — write them all down with their assembly description.',
      'Step 2: Measure LF of each partition type and apply average ceiling height to get SF (1-side) and SF (2-side).',
      'Step 3: Apply Gypsum Association GA-216 waste factors — typically 7% on board and 8% on framing.',
      'Step 4: Layer in joint compound (1 bucket per 100 SF as a rule of thumb), tape, corner bead and accessories.',
      'Step 5: Price labor at your local hanging + finishing rates. In the mid-Atlantic in 2026, expect $2.85–$3.45/SF installed for a standard Level 4 finish.',
    ],
  },
  {
    slug: 'cost-to-build-tiny-home-2026',
    title: 'Average Cost to Build a Tiny Home in 2026: Budget vs. Luxury',
    metaTitle: 'Cost to Build a Tiny Home 2026 | Budget & Luxury Options | Core Estimation',
    metaDesc: 'Realistic 2026 pricing to build a tiny home in the US — from $35K DIY shells to $190K luxury fully-finished units.',
    category: 'Residential Estimation',
    date: 'January 03, 2026',
    readTime: '5 min',
    excerpt: 'Building a tiny home in the U.S. can cost anywhere from $35,000 DIY to $190,000 turnkey luxury in 2026.',
    image: 'https://images.unsplash.com/photo-1778062374926-f8432665f099?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxidWlsZGluZyUyMHJlc2lkZW50aWFsfGVufDB8fHx8MTc3ODU4MTU0OXww&ixlib=rb-4.1.0&q=85',
    body: [
      'A 200 SF DIY shell-only tiny home on a trailer costs $35K–$50K in materials in 2026.',
      'A contractor-built 280 SF tiny home with standard finishes runs $85K–$120K turnkey.',
      'Luxury 400 SF tiny homes with high-end finishes, off-grid solar and smart-home integration land at $160K–$190K.',
      'Land, foundation (if not on a trailer), permits, and utility hookups are not included in any of the above and can easily add $25K–$80K.',
    ],
  },
];

export const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  {
    label: 'Services',
    path: '/services',
    children: services.slice(0, 8).map(s => ({ label: s.title, path: `/services/${s.slug}` })),
  },
  { label: 'Samples', path: '/samples' },
  { label: 'Case Studies', path: '/case-studies' },
  { label: 'Blog', path: '/blog' },
  { label: 'Contact', path: '/contact' },
];

export const iconSet = {
  HardHat, Wrench, Ruler, Building2, HomeIcon, Brush, Box, Cpu, FileSpreadsheet,
  Calculator, ShieldCheck, Clock, Users, Award, Target, TrendingUp, CheckCircle2,
  Briefcase, Trees, Layers, Zap, Hammer, ClipboardCheck, DollarSign,
  Activity, Gauge, FileText,
};
